"use client";

import * as React from "react";
import Link from "next/link";
import { motion } from "motion/react";
import {
  Trophy,
  Sparkles,
  Crown,
  Award,
  Globe2,
  Medal,
  Heart,
  ShieldCheck,
  Target,
  Building2,
  Lock,
  ArrowRight,
  Mic,
  BookOpen,
  Brain,
  Compass,
  Users,
  Flag,
} from "lucide-react";
import { PageHero } from "@/components/ui/page-hero";
import { Section } from "@/components/layout/section";
import { RelatedPages } from "@/components/ui/related-pages";
import { StepperBlock } from "@/lib/blocks/stepper-block";
import { CounterBlock } from "@/lib/blocks/counter-block";
import { PillarGridBlock } from "@/lib/blocks/pillar-grid-block";
import { TrendingUp, Quote } from "lucide-react";

const ACHIEVEMENT_LADDER = [
  {
    num: "01",
    title: "Foundation · Medical Operating System",
    body: "The MBBS with specialization in Gynaecology from Delhi. The installation of the clinical operating system — six principles that govern every engagement, every platform, every outcome. The beginning of the operational record.",
    accent: "red" as const,
  },
  {
    num: "02",
    title: "Trust · Sovereign-Grade Security",
    body: "Encrypted private networks for governments and their highest offices. Zero security incidents across every engagement. The cultural DNA of sovereign-grade security established — the foundation of every subsequent expansion.",
    accent: "purple" as const,
  },
  {
    num: "03",
    title: "Foresight · Predictive Intelligence",
    body: "Eighty-nine percent prediction accuracy. Two hundred-plus platforms monitored. The transition from defensive security to proactive intelligence. The gain of organizational foresight — the ability to see what is about to happen.",
    accent: "cyan" as const,
  },
  {
    num: "04",
    title: "Scale · Eighteen Countries, Three Continents",
    body: "Operations spanning sovereign-level clients across multiple political systems, regulatory frameworks, and cultural environments. Nine hundred million voters reached. Fifteen-plus languages. The cross-cultural operational fluency proven at scale.",
    accent: "emerald" as const,
  },
  {
    num: "05",
    title: "Recognition · Nobel Peace Prize, WEF, Sovereign Stages",
    body: "Presence at the most significant stages of international consequence. The 70% Project as flagship thought leadership. Fifteen campaign victories, over three hundred elite clients, zero security incidents. The verified record that places the work alongside those who shape global agendas.",
    accent: "amber-gold" as const,
  },
];

const RECOGNITION_STATS = [
  { value: 15, suffix: " / 18", label: "Campaign Victories", description: "Eighty-three point three percent success rate verified.", accent: "red" as const, icon: <TrendingUp className="size-5" /> },
  { value: 900, suffix: "M+", label: "Voters Reached", description: "Across twenty-two official languages.", accent: "purple" as const, icon: <Globe2 className="size-5" /> },
  { value: 0, suffix: "", label: "Security Incidents", description: "Fifteen-plus years of sovereign-grade security.", accent: "cyan" as const, icon: <ShieldCheck className="size-5" /> },
  { value: 99.9999, suffix: "%", label: "Uptime Maintained", description: "Across nine platforms and eighteen countries.", accent: "emerald" as const, icon: <Award className="size-5" /> },
  { value: 300, suffix: "+", label: "Elite Clients Served", description: "Nine client categories under confidentiality.", accent: "amber-gold" as const, icon: <Globe2 className="size-5" /> },
  { value: 70, suffix: "%", label: "Women in Teams", description: "By selection. By performance. By results.", accent: "denim" as const, icon: <Award className="size-5" /> },
];

const PEER_RECOGNITION = [
  { num: "01", title: "Among Operators", body: "The operational standard — nine platforms, sixty-plus services, eighteen countries, zero breaches — is not common. In the field of strategic sovereignty, where claims outpace results by orders of magnitude, the verified operational record distinguishes those who deliver from those who advertise.", accent: "red" as const },
  { num: "02", title: "Among Government Leaders", body: "The trust of sovereign-level clients is not extended to organizations that have not proven themselves at the highest stakes. The fact that the organization has sustained these relationships across fifteen-plus years is the strongest peer recognition.", accent: "purple" as const },
  { num: "03", title: "Among Technology Builders", body: "Nine proprietary platforms, zero third-party dependencies, a decade of in-house development. The peers who build technology recognize the magnitude of operating an integrated ecosystem refined through mission-critical deployment across eighteen countries.", accent: "cyan" as const },
  { num: "04", title: "Among Women Leaders", body: "The seventy percent women operating model is recognized not as a diversity initiative but as a talent strategy with a decade of evidence. The operating advantages are structural, not aspirational — communication, risk discipline, collaboration, retention, ego management.", accent: "emerald" as const },
];

const ACCENT: Record<string, { text: string; bg: string; border: string; ring: string; pill: string }> = {
  red: { text: "text-red", bg: "from-red/10", border: "border-red/30", ring: "ring-red/20", pill: "glass-tinted-red border-red/20" },
  purple: { text: "text-purple", bg: "from-purple/10", border: "border-purple/30", ring: "ring-purple/20", pill: "glass-tinted-purple border-purple/20" },
  cyan: { text: "text-cyan", bg: "from-cyan/10", border: "border-cyan/30", ring: "ring-cyan/20", pill: "glass-tinted-cyan border-cyan/20" },
  emerald: { text: "text-emerald", bg: "from-emerald/10", border: "border-emerald/30", ring: "ring-emerald/20", pill: "glass-tinted-emerald border-emerald/20" },
  "amber-gold": { text: "text-amber-gold", bg: "from-amber-gold/10", border: "border-amber-gold/30", ring: "ring-amber-gold/20", pill: "glass-tinted-amber-gold border-amber-gold/20" },
  denim: { text: "text-denim", bg: "from-denim/10", border: "border-denim/30", ring: "ring-denim/20", pill: "glass-tinted-denim border-denim/20" },
  lime: { text: "text-lime", bg: "from-lime/10", border: "border-lime/30", ring: "ring-lime/20", pill: "glass-tinted-lime border-lime/20" },
  coral: { text: "text-coral", bg: "from-coral/10", border: "border-coral/30", ring: "ring-coral/20", pill: "glass-tinted-coral border-coral/20" },
};

const PRESTIGE_STAGES = [
  {
    icon: Medal,
    title: "The Nobel Peace Prize",
    body: "Presence at the Nobel Peace Prize — a stage reserved for those operating at the highest levels of international consequence. Not a speaking engagement to be claimed casually. An indicator of the caliber of company kept and the weight of the work done. The annual convergence of individuals and organizations whose decisions affect millions of lives.",
    accent: "amber-gold",
  },
  {
    icon: Globe2,
    title: "The World Economic Forum",
    body: "Presence at the World Economic Forum — the operating table of global governance. Where heads of state, Fortune 100 CEOs, central bank governors, and leaders of international organizations convene. The Forum&apos;s agenda covers artificial intelligence, cybersecurity, digital governance, geopolitical risk, and the future of work — all domains where the operational expertise directly applies.",
    accent: "cyan",
  },
];

const CAMPAIGN_ACHIEVEMENTS = [
  { metric: "15/18", label: "Campaign Victories", sub: "Eighty-three point three percent success rate", accent: "red" },
  { metric: "900M+", label: "Voters Reached", sub: "Across 22 official languages", accent: "purple" },
  { metric: "300+", label: "Elite Clients Served", sub: "Nine client categories", accent: "cyan" },
  { metric: "18", label: "Countries", sub: "Three continents", accent: "emerald" },
  { metric: "99.9999%", label: "Operational Uptime", sub: "31.5 seconds max/year", accent: "amber-gold" },
  { metric: "0", label: "Security Incidents", sub: "15+ years clean record", accent: "denim" },
];

const ACHIEVEMENT_CATEGORIES = [
  {
    icon: Trophy,
    title: "Campaign Achievements",
    body: "Fifteen verified victories out of eighteen international campaign engagements — an eighty-three point three percent success rate in a domain where the average hovers near fifty percent. The campaigns operate under absolute operational discipline, leaving no trace detectable by competitors, media, or the public. Every campaign follows the five-step methodology: Discovery, Research, Monitor, Analysis, Execution.",
    accent: "red",
    details: [
      "15 victories out of 18 engagements",
      "83.3% success rate verified",
      "900M+ voters reached",
      "22 official languages",
      "Hundreds of thousands of polling booths",
      "Five-step methodology enforced",
    ],
  },
  {
    icon: Globe2,
    title: "Operational Achievements",
    body: "Oversight of the operational execution of a technology ecosystem that includes the integrated platform ecosystem and sixty-plus integrated services across five interconnected domains. The operational achievement is not merely the existence of these capabilities — it is their coordinated deployment across eighteen countries with zero security breaches and ninety-nine point nine nine nine nine percent uptime.",
    accent: "purple",
    details: [
      "Integrated platform ecosystem",
      "60+ integrated services",
      "5 interconnected domains",
      "Zero security breaches",
      "99.9999% uptime verified",
      "Zero third-party dependencies",
    ],
  },
  {
    icon: Users,
    title: "Team Building Achievements",
    body: "The teams are, in most offices, about seventy percent women. Not a diversity initiative — an operating decision with a decade of evidence behind it. The structural advantages: better communication, stronger risk discipline, collaboration over competition, higher retention, and less ego interference with execution. Verified through fifteen campaign victories, over three hundred elite clients, and zero security incidents.",
    accent: "emerald",
    details: [
      "70% women by selection · not target",
      "5 structural operating advantages",
      "Verified through 15 campaign victories",
      "Zero security incidents · 15+ years",
      "100% client satisfaction",
      "5+ year average tenure",
    ],
  },
  {
    icon: Building2,
    title: "Governance Achievements",
    body: "Overseen governance transformation programs across eighteen countries — processing data for over nine hundred million citizens. The governance achievement is not the deployment of technology — it is the measurable improvement in policy implementation, citizen satisfaction, and service delivery. The four pillars: Digital Infrastructure, Data Intelligence, Process Reform, Citizen Engagement.",
    accent: "cyan",
    details: [
      "18 countries · governance deployed",
      "900M+ citizens served",
      "4 governance pillars",
      "60+ governance capabilities",
      "Operational within months",
      "Complete sovereignty stack",
    ],
  },
  {
    icon: Lock,
    title: "Security Achievements",
    body: "Zero security breaches across all engagements, across fifteen-plus years of sovereign-grade operations. Not zero breaches last year. Not zero breaches in the last five years. Zero breaches across the entire operational history. The security architecture is sovereign-grade, designed to withstand nation-state level threats. The compliance posture spans every major regulatory regime.",
    accent: "amber-gold",
    details: [
      "Zero security breaches · ever",
      "15+ years verified",
      "Quantum-resistant encryption",
      "7-layer zero-trust defense",
      "Air-gap deployment capability",
      "GDPR · HIPAA · ISO 27001 · SOC 2",
    ],
  },
  {
    icon: Flag,
    title: "Global Achievements",
    body: "Part of operations that reached nine hundred million voters across eighteen countries on three continents. The operational discipline required to deliver sovereign-grade outcomes across eighteen different political systems, regulatory frameworks, communication norms, and cultural environments is the measure of cross-cultural operational fluency.",
    accent: "denim",
    details: [
      "900M+ voters reached",
      "18 countries · active operations",
      "3 continents of presence",
      "30+ countries served total",
      "7 primary regions",
      "45 US states with engagements",
    ],
  },
];

const THOUGHT_LEADERSHIP = [
  { icon: Heart, title: "The 70% Project", body: "The flagship initiative documenting the operating case — backed by a decade of evidence — for building teams that are over seventy percent women. From evidence to movement — from documenting the case to building the infrastructure that makes it the default.", accent: "red" },
  { icon: BookOpen, title: "The Operator's Notebook", body: "A biweekly publication offering one disciplined note on how excellent things run. No filler. No slogans. One principle, made usable, every two weeks. The operating principles that govern the integrated ecosystem — distilled into something applicable to any organization.", accent: "purple" },
  { icon: Brain, title: "The Body of Writing", body: "Essays on operational excellence, leadership as diagnosis, the 70% operating model, and building in the dark. The clinical method applied to organizational leadership — the argument documented in prose: the skills that save lives in a delivery room save organizations from self-destruction in the boardroom.", accent: "cyan" },
  { icon: Mic, title: "The Speaking Record", body: "Presence at the Nobel Peace Prize and the World Economic Forum. Topics include operational excellence, leadership as diagnosis, the 70% operating model, building in the dark, the triple threat, and women in the C-suite. The speaking record is measured in the caliber of the stages and the weight of the audiences.", accent: "amber-gold" },
];

const CLIENT_CATEGORIES = [
  "Presidential & Prime Ministerial Offices",
  "Governments",
  "Royal Families",
  "Political Leaders",
  "Defense Agencies",
  "Fortune-Scale Corporations",
  "High-Net-Worth Individuals",
  "International Organizations",
  "Political Movements",
];

export default function RecognitionPage() {
  return (
    <>
      <PageHero
        icon={Trophy}
        overline="Recognition & Achievements"
        overlineClassName="text-amber-gold"
        title={
          <>
            Verified Achievements at <span className="text-gradient-premium">Sovereign Scale.</span>
          </>
        }
        subtitle="The complete record of what has been built, what has been achieved, and what has been recognized. Not claimed — verified. Measured in campaign victories, security incidents avoided, uptime maintained, and governance outcomes delivered."
        breadcrumbs={[{ label: "Home", href: "/" }, { label: "About", href: "/about/" }, { label: "Recognition" }]}
        badge="Verified outcomes · 15+ years"
      />

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-5`}>
            <Sparkles className="size-3.5 text-amber-gold" />
            <span className="text-overline text-amber-gold">Executive Summary</span>
          </div>
          <h2 className="text-display-md mb-5">The accumulated weight of a decade of verified delivery.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            Recognition is not a collection of awards placed on a shelf. It is the accumulated weight of a decade of verified operational delivery — fifteen victories out of eighteen international campaign engagements, an eighty-three point three percent success rate that is unmatched in the field. The integrated platform ecosystem operating in coordinated harmony across eighteen countries. Zero security breaches across fifteen-plus years of sovereign-grade operations.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            Professional standing has been acknowledged at the highest levels of international consequence. Presence at the Nobel Peace Prize and the World Economic Forum places the work alongside the individuals and organizations that shape global agendas. Every metric below traces to verified operational data. Not projections. Not targets. Results.
          </p>
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <Trophy className="size-3.5 text-red" />
            <span className="text-overline text-red">Achievement Ladder</span>
          </div>
          <h2 className="text-display-md mb-4">Five stages from operating system to sovereign recognition.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Recognition is the accumulated evidence of a career. Each stage built the foundation for the next — from medical training through sovereign-grade security, predictive intelligence, sovereign scale, and international standing.
          </p>
        </div>
        <StepperBlock steps={ACHIEVEMENT_LADDER} orientation="vertical" />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.red.pill} mb-4`}>
            <Medal className="size-3.5 text-red" />
            <span className="text-overline text-red">Prestige Stages</span>
          </div>
          <h2 className="text-display-md mb-4">Recognition among those who shape the world.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The two most significant stages of international consequence where the work has been recognized. They are not the only markers of standing — they are the most visible.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-5">
          {PRESTIGE_STAGES.map((p, i) => {
            const a = ACCENT[p.accent];
            return (
              <motion.div
                key={p.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className={`group glass-card rounded-2xl p-7 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="flex items-start gap-4">
                  <div className={`size-14 rounded-2xl bg-gradient-sovereign flex items-center justify-center shadow-xl ring-2 ${a.ring}`}>
                    <p.icon className="size-7 text-white" />
                  </div>
                  <div>
                    <span className={`text-overline ${a.text}`}>Stage of International Consequence</span>
                    <h3 className={`text-display-sm ${a.text} mb-3`}>{p.title}</h3>
                    <p className="text-body-md text-muted-foreground leading-relaxed">{p.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.cyan.pill} mb-4`}>
            <Trophy className="size-3.5 text-cyan" />
            <span className="text-overline text-cyan">Headline Achievements</span>
          </div>
          <h2 className="text-display-md mb-4">The verified headline numbers.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Six metrics that summarize a decade of sovereign-scale operations. Each verified by the clients who continue to engage and the referrals that sustain the practice.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {CAMPAIGN_ACHIEVEMENTS.map((m, i) => {
            const a = ACCENT[m.accent];
            return (
              <motion.div
                key={m.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className={`glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.01] transition-all`}
              >
                <div className={`text-display-md font-bold ${a.text} leading-none mb-3`}>{m.metric}</div>
                <div className="text-caption font-bold uppercase tracking-wider mb-1">{m.label}</div>
                <div className="text-caption text-muted-foreground">{m.sub}</div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <TrendingUp className="size-3.5 text-purple" />
            <span className="text-overline text-purple">The Verified Record</span>
          </div>
          <h2 className="text-display-md mb-4">The numbers behind the recognition.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Six metrics that summarize a decade of sovereign-scale operations. Each verified by the clients who continue to engage and the referrals that sustain the practice.
          </p>
        </div>
        <CounterBlock stats={RECOGNITION_STATS} columns={3} />
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-5`}>
            <Quote className="size-3.5 text-denim" />
            <span className="text-overline text-denim">The NINJA Doctrine</span>
          </div>
          <h2 className="text-display-md mb-4">Absolute silence. No noise. No exposure. No footprints.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            The campaigns operate under the Political NINJA Doctrine. Campaign activity leaves no trace detectable by competitors, media, or the public. Intelligence assets operate without attribution. Content amplification emerges organically from the community rather than from identifiable campaign sources. The campaign&apos;s hand is never visible at any stage — from intelligence gathering through execution to post-victory dissolution.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            The operational method follows the five-step methodology — Discovery, Research, Monitor, Analysis, Execution. The same methodology governs every engagement, every platform, every geography. The consistency of the method is what produces the consistency of the outcomes. Fifteen victories out of eighteen engagements is not luck. It is the verified result of a clinical operating system applied to political strategy at sovereign scale — the same operating system that produced eighty-nine percent prediction accuracy on the intelligence engine and ninety-nine point nine nine nine nine percent uptime across the entire ecosystem.
          </p>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.emerald.pill} mb-4`}>
            <Award className="size-3.5 text-emerald" />
            <span className="text-overline text-emerald">Achievement Categories</span>
          </div>
          <h2 className="text-display-md mb-4">Six categories of verified achievement.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            From campaign victories to governance transformation, from team building to security — the complete record of what has been achieved, what has been sustained, and what has been recognized.
          </p>
        </div>
        <div className="space-y-4">
          {ACHIEVEMENT_CATEGORIES.map((a, i) => {
            const ac = ACCENT[a.accent];
            return (
              <motion.div
                key={a.title}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.06 }}
                className={`group glass-card rounded-2xl p-6 lg:p-7 bg-gradient-to-br ${ac.bg} to-transparent border ${ac.border} hover:scale-[1.005] transition-all`}
              >
                <div className="grid lg:grid-cols-[auto_1fr_auto] gap-6 items-start">
                  <div className={`size-14 rounded-2xl bg-gradient-sovereign flex items-center justify-center shadow-xl ring-2 ${ac.ring} group-hover:scale-110 group-hover:rotate-3 transition-all`}>
                    <a.icon className="size-7 text-white" />
                  </div>
                  <div>
                    <span className={`text-overline ${ac.text}`}>Category {String(i + 1).padStart(2, "0")}</span>
                    <h3 className={`text-display-sm mb-2 ${ac.text}`}>{a.title}</h3>
                    <p className="text-body-md text-muted-foreground leading-relaxed mb-4">{a.body}</p>
                  </div>
                  <ul className="space-y-1.5 lg:max-w-[280px]">
                    {a.details.map((d) => (
                      <li key={d} className="flex items-start gap-2 text-caption text-muted-foreground">
                        <ShieldCheck className={`size-3.5 mt-0.5 shrink-0 ${ac.text}`} />
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-6">
          <div>
            <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT["amber-gold"].pill} mb-5`}>
              <Target className="size-3.5 text-amber-gold" />
              <span className="text-overline text-amber-gold">Client Categories</span>
            </div>
            <h2 className="text-display-md mb-4">The trust of sovereign-level clients.</h2>
            <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
              The recognition that matters most is not public. It is the trust of the clients who return — the sovereign-level entities who, having experienced the operational standard, choose to engage again, and again, and refer others. National security level confidentiality governs every engagement.
            </p>
            <p className="text-body-md text-muted-foreground leading-relaxed">
              Over three hundred elite clients served — a number that reflects not merely acquisition but retention. In the sovereign-grade space, trust is the only currency that matters. The clients who return are the clients who have experienced the operational standard.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            {CLIENT_CATEGORIES.map((c, i) => (
              <motion.div
                key={c}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.3, delay: i * 0.04 }}
                className="glass-card rounded-xl p-4 bg-gradient-to-br from-amber-gold/10 to-transparent border border-amber-gold/20 hover:scale-[1.01] transition-all"
              >
                <div className="flex items-center gap-2.5">
                  <ShieldCheck className="size-4 text-amber-gold shrink-0" />
                  <span className="text-body-sm font-semibold">{c}</span>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.purple.pill} mb-4`}>
            <BookOpen className="size-3.5 text-purple" />
            <span className="text-overline text-purple">Thought Leadership</span>
          </div>
          <h2 className="text-display-md mb-4">Documenting the operating method for the world.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            Thought leadership extends beyond operational delivery into the documentation and dissemination of the operating method itself. The body of work is not marketing material. It is the documentation of an operating system proven at sovereign scale.
          </p>
        </div>
        <div className="grid md:grid-cols-2 gap-4">
          {THOUGHT_LEADERSHIP.map((t, i) => {
            const a = ACCENT[t.accent];
            return (
              <motion.div
                key={t.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: i * 0.08 }}
                className={`group glass-card rounded-2xl p-6 bg-gradient-to-br ${a.bg} to-transparent border ${a.border} hover:scale-[1.005] transition-all`}
              >
                <div className="flex items-start gap-4">
                  <div className={`size-12 rounded-xl bg-gradient-sovereign flex items-center justify-center shadow-lg ring-2 ${a.ring} shrink-0`}>
                    <t.icon className="size-6 text-white" />
                  </div>
                  <div>
                    <h3 className={`text-heading-md mb-2 ${a.text}`}>{t.title}</h3>
                    <p className="text-body-sm text-muted-foreground leading-relaxed">{t.body}</p>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </div>
      </Section>

      <Section background="default" className="py-16 lg:py-20">
        <div className="text-center mb-12">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.denim.pill} mb-4`}>
            <Users className="size-3.5 text-denim" />
            <span className="text-overline text-denim">Peer Recognition</span>
          </div>
          <h2 className="text-display-md mb-4">Standing among operators, leaders, and builders.</h2>
          <p className="text-body-md text-muted-foreground max-w-2xl mx-auto">
            The standing among peers is established through the quality of the work and the consistency of the outcomes. Four constituencies recognize the difference the operating method produces.
          </p>
        </div>
        <PillarGridBlock pillars={PEER_RECOGNITION} columns={2} variant="numbered" />
      </Section>

      <Section background="muted" className="py-16 lg:py-20">
        <div className="max-w-4xl mx-auto">
          <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full ${ACCENT.lime.pill} mb-5`}>
            <Sparkles className="size-3.5 text-lime" />
            <span className="text-overline text-lime">Why Recognition Matters</span>
          </div>
          <h2 className="text-display-md mb-4">The difference between claimed and proven.</h2>
          <p className="text-body-md text-muted-foreground leading-relaxed mb-5">
            In an industry where claims outpace results by orders of magnitude, verified recognition carries a weight that cannot be manufactured. Dr. Jyoti Kush&apos;s achievements matter because they are proven — measured in outcomes that cannot be faked, metrics that cannot be inflated, and a track record that has been tested across fifteen-plus years of sovereign-grade operations. The zero-breach record, the eighty-three point three percent campaign success rate, the ninety-nine point nine nine nine nine percent uptime — these are not statistics. They are the proof that the trust placed in Dr. Kush&apos;s organization is warranted.
          </p>
          <p className="text-body-md text-muted-foreground leading-relaxed">
            In the sovereign-grade space, trust is not earned through promises. It is earned through the demonstrated capacity to deliver under conditions where failure has consequences measured in national stability. The verified achievements documented on this page are the currency of that trust. Every campaign victory adds to the evidence base. Every year of zero breaches deepens the trust. Every platform deployed across a new country extends the proof. The competitive advantage is not a snapshot. It is a trajectory — and the trajectory points consistently upward.
          </p>
        </div>
      </Section>

      <Section background="default" className="py-20 lg:py-28">
        <div className="relative">
          <div aria-hidden className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-amber-gold/15 blur-3xl pointer-events-none" />
          <div aria-hidden className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-emerald/15 blur-3xl pointer-events-none" />
          <div className="relative glass-strong rounded-3xl p-8 md:p-12 lg:p-16 gradient-border text-center">
            <Trophy className="size-10 text-amber-gold mx-auto mb-4" />
            <h2 className="text-display-md mb-3">The recognition that matters most.</h2>
            <p className="text-body-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              The recognition that matters most is not public. It is the trust of the clients who return. For qualified introductions seeking deeper engagement, the contact channels are open.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                href="/contact/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md bg-gradient-sovereign text-white font-semibold shadow-lg hover:scale-105 transition-transform"
              >
                <Mic className="size-4" />
                Engage Dr. Jyoti Kush
                <ArrowRight className="size-4" />
              </Link>
              <Link
                href="/about/credentials/"
                className="inline-flex items-center justify-center gap-2 h-12 px-6 rounded-md glass font-semibold hover:scale-105 transition-transform"
              >
                <Award className="size-4" />
                View Credentials
              </Link>
            </div>
          </div>
        </div>
      </Section>

      <RelatedPages
        title="Continue exploring"
        items={[
          { title: "About Dr. Jyoti Kush", description: "The full story. Physician. Operator. Architect of sovereign-scale operations.", href: "/about/", icon: Crown, accent: "red" },
          { title: "Credentials", description: "Medical foundation and operational track record.", href: "/about/credentials/", icon: Award, accent: "purple" },
          { title: "Philosophy", description: "The clinical method applied to organizations.", href: "/about/philosophy/", icon: Brain, accent: "emerald" },
          { title: "Frequently Asked", description: "Every question answered, every claim verified.", href: "/about/frequently-asked/", icon: Compass, accent: "amber-gold" },
        ]}
      />
    </>
  );
}
